/**
 * Pipeline (kanban) state. Workspace-scoped rows from the `pipelines` table —
 * each row is one kanban column: { id, title, order, color }. Stages are
 * seeded on workspace creation by the seed_workspace_default_pipelines RPC,
 * and are user-editable from the Manage Columns dialog.
 *
 * NOTE: until the user runs `pnpm supabase gen types`, the generated Database
 * type does not include the `color`, `default_checklists`, or
 * `pipeline_property_checklists` additions. We cast at the query level rather
 * than the whole client to keep the rest of the file type-safe.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import { useWorkspace } from "@/hooks/use-workspace";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";
import { PIPELINE_COLOR_PALETTE } from "@/lib/default-pipelines";
import { isProtectedPipeline } from "@/lib/pipeline-display";
import {
  STAGE_HISTORY_QUERY_PREFIX,
  stageHistoryQueryKey,
} from "@/lib/query-keys";
import type { PipelineRow } from "@/types/pipeline";

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

export interface StageHistoryEntry {
  id: string;
  pipeline_property_id: string;
  from_pipeline_id: string | null;
  to_pipeline_id: string | null;
  changed_at: string;
  notes: string | null;
}

interface CreatePipelineInput {
  title: string;
  color?: string;
}

interface UpdatePipelineInput {
  id: string;
  patch: Partial<Pick<PipelineRow, "title" | "color" | "order">>;
}

interface ReorderInput {
  /** Ordered list of pipeline IDs in their new display order. */
  ids: string[];
}

export function usePipeline() {
  const {
    savedProperties,
    isLoading: savedLoading,
    refetch: refetchSaved,
    removeDeal,
  } = useSavedProperties();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  /* ──────────────────────────────────────────────────────────────────── *
   *  Pipelines (stages) — fetched from DB
   * ──────────────────────────────────────────────────────────────────── */
  const pipelinesQuery = useQuery({
    queryKey: ["pipelines", currentWorkspace?.id] as const,
    enabled: Boolean(currentWorkspace?.id),
    queryFn: async () => {
      if (!currentWorkspace?.id) return [] as PipelineRow[];
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (
                col: string,
                v: string,
              ) => {
                order: (col: string) => DbResp<PipelineRow[]>;
              };
            };
          };
        }
      )
        .from("pipelines")
        .select("*")
        .eq("workspace_id", currentWorkspace.id)
        .order("order")) as PostgrestSingleResponse<PipelineRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  const pipelines: PipelineRow[] = pipelinesQuery.data ?? [];

  const invalidatePipelines = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ["pipelines", currentWorkspace?.id],
    });
  }, [queryClient, currentWorkspace?.id]);

  /* ──────────────────────────────────────────────────────────────────── *
   *  Stage history (audit log)
   * ──────────────────────────────────────────────────────────────────── */
  /**
   * Stable key derived from the set of saved-property IDs. Reference-changes
   * to `savedProperties` (e.g. an optimistic card move that only flips
   * `pipeline_id`) leave this string identical, so the query key is unchanged
   * and the history is served from cache — no refetch per drag. Without this,
   * every move re-fetched and toggled the loading flag true→false twice,
   * flashing the kanban skeleton (see Pipeline.tsx's `isLoading` gate).
   */
  const savedIdsKey = useMemo(
    () => savedProperties.map((p) => p.id).sort().join("|"),
    [savedProperties],
  );

  /**
   * The audit log lives in the query cache, NOT `useState` + `useEffect`.
   * `usePipeline` is instantiated many times per screen (Pipeline page +
   * KanbanBoard + ManageColumnsDialog, Dashboard, the portfolio/section-8
   * hooks…) and most of those consumers never read `stageHistory` — with
   * local state each instance fired its own identical GET. One cache entry
   * per (workspace, deal-id set) means one request shared by all of them,
   * and writers refresh it by invalidating the key.
   */
  const stageHistoryQuery = useQuery({
    queryKey: stageHistoryQueryKey(currentWorkspace?.id, savedIdsKey),
    enabled: Boolean(user?.id && currentWorkspace?.id && savedIdsKey),
    queryFn: async () => {
      const savedIds = savedIdsKey ? savedIdsKey.split("|") : [];
      if (savedIds.length === 0) return [] as StageHistoryEntry[];
      const { data, error } = await supabase
        .from("pipeline_stage_history")
        .select("*")
        .in("pipeline_property_id", savedIds)
        .order("changed_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as StageHistoryEntry[];
    },
  });

  const stageHistory = useMemo<StageHistoryEntry[]>(
    () => stageHistoryQuery.data ?? [],
    [stageHistoryQuery.data],
  );

  /**
   * Refresh the audit log after a write. Matches by prefix so every deal-id-set
   * variant is invalidated — a writer that just added a deal doesn't know which
   * id set the mounted consumers are keyed on.
   */
  const invalidateStageHistory = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: STAGE_HISTORY_QUERY_PREFIX,
    });
  }, [queryClient]);

  /* ──────────────────────────────────────────────────────────────────── *
   *  Deal placement helpers
   * ──────────────────────────────────────────────────────────────────── */

  /** Filter saved properties to those that belong on the kanban of `pipelineId`. */
  const getPropertiesByPipeline = useCallback(
    (pipeline: PipelineRow) => {
      return savedProperties.filter((p) => {
        const row = p as unknown as { pipeline_id?: string | null };
        return row.pipeline_id === pipeline.id;
      });
    },
    [savedProperties],
  );

  /**
   * Move a deal to a different pipeline. Does NOT enforce the required-
   * checklist gate — use the `moveDeal` mutation in `use-pipeline-mutations`
   * for drag/drop flows that need gating. This bare helper is used by
   * non-gated flows (manual move via menu, "Move to Closed Lost", etc.).
   */
  const moveToPipeline = useCallback(
    async (savedPropertyId: string, target: PipelineRow) => {
      try {
        const current = savedProperties.find((p) => p.id === savedPropertyId);
        const currentRow = current as unknown as
          | { pipeline_id?: string | null }
          | undefined;
        const fromPipelineId = currentRow?.pipeline_id ?? null;

        const { data, error } = await supabase
          .from("pipeline_properties")
          .update({
            pipeline_id: target.id,
          } as never)
          .eq("id", savedPropertyId)
          .select("id");
        if (error) throw error;
        assertRowsAffected(data, "move this pipeline item");

        if (user?.id) {
          await supabase.from("pipeline_stage_history").insert({
            pipeline_property_id: savedPropertyId,
            changed_by: user.id,
            from_pipeline_id: fromPipelineId,
            to_pipeline_id: target.id,
            workspace_id: currentWorkspace?.id ?? null,
          } as never);
        }

        toast.success(`Moved to ${target.title}`);
        await refetchSaved();
        invalidateStageHistory();
      } catch (error) {
        console.error("Error moving property:", error);
        toast.error(toUserMessage(error, "Failed to move property"));
      }
    },
    [
      refetchSaved,
      invalidateStageHistory,
      user?.id,
      savedProperties,
      currentWorkspace?.id,
    ],
  );

  const getHistoryForProperty = useCallback(
    (savedPropertyId: string) =>
      stageHistory.filter((h) => h.pipeline_property_id === savedPropertyId),
    [stageHistory],
  );

  const updateStageNote = useCallback(
    async (historyId: string, notes: string) => {
      try {
        const { data, error } = await supabase
          .from("pipeline_stage_history")
          .update({ notes } as never)
          .eq("id", historyId)
          .select("id");
        if (error) throw error;
        assertRowsAffected(data, "edit this stage note");
        toast.success("Note saved");
        invalidateStageHistory();
      } catch (e) {
        console.error("Error saving note:", e);
        toast.error(toUserMessage(e, "Failed to save note"));
      }
    },
    [invalidateStageHistory],
  );

  const updatePropertyNotes = useCallback(
    async (savedPropertyId: string, notes: string) => {
      try {
        const { data, error } = await supabase
          .from("pipeline_properties")
          .update({ notes } as never)
          .eq("id", savedPropertyId)
          .select("id");
        if (error) throw error;
        assertRowsAffected(data, "edit notes on this pipeline item");
        toast.success("Note saved");
        await refetchSaved();
      } catch (e) {
        console.error("Error saving note:", e);
        toast.error(toUserMessage(e, "Failed to save note"));
      }
    },
    [refetchSaved],
  );

  /* ──────────────────────────────────────────────────────────────────── *
   *  Pipeline (stage) CRUD — used by Manage Columns dialog
   * ──────────────────────────────────────────────────────────────────── */

  const createPipeline = useMutation({
    mutationFn: async (input: CreatePipelineInput) => {
      if (!currentWorkspace?.id || !user?.id) {
        throw new Error("No active workspace");
      }
      const nextOrder =
        pipelines.length > 0
          ? Math.max(...pipelines.map((p) => p.order)) + 1
          : 1;
      const usedColors = new Set(pipelines.map((p) => p.color).filter(Boolean));
      const nextColor =
        input.color ??
        PIPELINE_COLOR_PALETTE.find((c) => !usedColors.has(c)) ??
        PIPELINE_COLOR_PALETTE[0];
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            insert: (v: unknown) => {
              select: (s: string) => { single: () => DbResp<PipelineRow> };
            };
          };
        }
      )
        .from("pipelines")
        .insert({
          workspace_id: currentWorkspace.id,
          title: input.title,
          order: nextOrder,
          color: nextColor,
          created_by: user.id,
        })
        .select("*")
        .single()) as PostgrestSingleResponse<PipelineRow>;
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidatePipelines();
      toast.success("Pipeline added");
    },
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to add pipeline")),
  });

  const updatePipeline = useMutation({
    mutationFn: async (input: UpdatePipelineInput) => {
      const { error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            update: (v: unknown) => {
              eq: (col: string, v: string) => DbResp<unknown>;
            };
          };
        }
      )
        .from("pipelines")
        .update(input.patch)
        .eq("id", input.id)) as PostgrestSingleResponse<unknown>;
      if (error) throw error;
    },
    onSuccess: invalidatePipelines,
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to update pipeline")),
  });

  const deletePipeline = useMutation({
    mutationFn: async (id: string) => {
      const target = pipelines.find((p) => p.id === id);
      if (target && isProtectedPipeline(target)) {
        throw new Error(`"${target.title}" is a system column and can't be deleted`);
      }
      const { error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            delete: () => {
              eq: (col: string, v: string) => DbResp<unknown>;
            };
          };
        }
      )
        .from("pipelines")
        .delete()
        .eq("id", id)) as PostgrestSingleResponse<unknown>;
      if (error) throw error;
    },
    onSuccess: () => {
      invalidatePipelines();
      void queryClient.invalidateQueries({ queryKey: ["pipeline_properties"] });
      toast.success("Pipeline removed");
    },
    onError: (err: unknown) =>
      toast.error(toUserMessage(err, "Failed to remove pipeline")),
  });

  /**
   * Bulk-update `order` for a list of pipeline IDs (reorder).
   *
   * Optimistic: writes the new order into the TanStack Query cache in
   * `onMutate` before any network call so the kanban / Manage Columns dialog
   * update instantly — without this, the cached array still holds the OLD
   * order during the N sequential UPDATEs, causing a visible snap-back.
   */
  const reorderPipelines = useMutation({
    mutationFn: async (input: ReorderInput) => {
      // Run sequentially so the responses interleave less; small N (≈9).
      for (let i = 0; i < input.ids.length; i++) {
        const id = input.ids[i];
        const { error } = (await (
          supabase as unknown as {
            from: (t: string) => {
              update: (v: unknown) => {
                eq: (col: string, v: string) => DbResp<unknown>;
              };
            };
          }
        )
          .from("pipelines")
          .update({ order: i + 1 })
          .eq("id", id)) as PostgrestSingleResponse<unknown>;
        if (error) throw error;
      }
    },
    onMutate: async (input: ReorderInput) => {
      const key = ["pipelines", currentWorkspace?.id] as const;
      // Stop any in-flight refetch from clobbering the optimistic order.
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PipelineRow[]>(key);
      if (previous) {
        const byId = new Map(previous.map((p) => [p.id, p]));
        const optimistic = input.ids
          .map((id, idx) => {
            const row = byId.get(id);
            return row ? { ...row, order: idx + 1 } : null;
          })
          .filter((p): p is PipelineRow => p !== null);
        queryClient.setQueryData<PipelineRow[]>(key, optimistic);
      }
      return { previous };
    },
    onError: (err: unknown, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["pipelines", currentWorkspace?.id],
          context.previous,
        );
      }
      toast.error(toUserMessage(err, "Failed to reorder pipelines"));
    },
    // Refetch on both success and error so the cache reconciles with the
    // server (and the rollback above gets confirmed against actual DB state).
    onSettled: invalidatePipelines,
  });

  return {
    /* Stages */
    pipelines,
    isLoadingPipelines: pipelinesQuery.isLoading,
    refetchPipelines: pipelinesQuery.refetch,
    createPipeline,
    updatePipeline,
    deletePipeline,
    reorderPipelines,

    /* Properties / deals */
    savedProperties,
    /**
     * Deals + stages — what the kanban needs. Stage history is deliberately
     * NOT part of this: only the analytics/timeline read it, and gating the
     * board on it delayed KanbanBoard's mount behind a request it never uses.
     */
    isLoading: savedLoading || pipelinesQuery.isLoading,
    getPropertiesByPipeline,
    moveToPipeline,
    /** Delete one deal card by `pipeline_properties.id`. */
    removeDeal,
    refetch: refetchSaved,

    /* Stage history */
    stageHistory,
    /** For consumers that render from `stageHistory` (analytics, timeline). */
    isLoadingStageHistory: stageHistoryQuery.isLoading,
    getHistoryForProperty,
    invalidateStageHistory,
    updateStageNote,
    updatePropertyNotes,
  };
}
